/*
 * The drive's addresses, and the way it navigates to them (S-06 §3.1). Every link the drive draws — a
 * card, a row, a breadcrumb, a quick-switcher row, a toast's Open — is built here, so the routes and the
 * links are one set of words written once.
 *
 * `/drive` opens Recent; `/drive/<key>` opens whatever the key names — a space, a folder, a document or
 * a file, a trailing `/<slug>` ignored — and the fixed words below are pages of their own. A fixed word
 * can never be a key (a key is eight symbols of the look-alike-free alphabet, `wire.KeyPattern`), and
 * the router ranks a static segment above a parameter anyway. The administrators' Other shared drives
 * lives under core's administration frame, at `/admin/drive`.
 *
 * **A document's address is its link id's** (DV W-1 D1): `/drive/<link id>[/<slug>][?tab=<tab key>]`,
 * one address for the team and for anybody it shares the document with, so the address a member sees
 * is the address that is shared. A member who arrives by the key is moved to it (`open.tsx`); chips,
 * search hits, Recent and notifications keep `/drive/<key>`, which keeps working. A tab other than the
 * first is named by `?tab=` (W2-D6), never by a segment, so the ignored slug keeps its place. This
 * file only tells the two shapes apart, since the routes are in the shell's chunk; the addresses
 * built from a link id are `addresses.ts`'s, which only the lazy pages reach.
 *
 * The navigate holder itself is the shell's (`core/shell/navigate.ts`); it is re-exported so that a
 * surface of this module reaches its navigation through its own module.
 */

import { KeyPattern, PathPrefix, type ItemView } from "./wire/drive.gen.ts"

export { navigateTo } from "../../core/shell/index.ts"

/**
 * The pages under `/drive/` that are words rather than keys, each with what it shows. The route table
 * is derived from this one record, so a new page is a line here.
 */
export const DRIVE_PAGES = {
	recent: "recent",
	starred: "starred",
	trash: "trash",
	my: "my",
	search: "search",
} as const

/** One of the fixed pages. */
export type DrivePage = keyof typeof DRIVE_PAGES

/** Where the drive opens: Recent. */
export const DRIVE_HOME = PathPrefix

/** The segment of core's administration frame the administrators' Other shared drives stands on. */
export const ADMIN_DRIVE_SEGMENT = "drive"

const KEY = new RegExp(KeyPattern)

/** Whether an address segment can be a space's or an item's key at all. */
export function isDriveKey(segment: string): boolean {
	return KEY.test(segment)
}

/**
 * Whether an address segment is a document's link id (W-1 D2): 32 lower-case hex characters, which
 * its length alone tells from a key. Core's `links.ts` reads a pasted address by the same shape.
 */
export function isLinkId(segment: string): boolean {
	return /^[0-9a-f]{32}$/.test(segment)
}

/** Whether an item is the one an address names, by its key or, for a document, its link id. */
export function namedBy(item: Pick<ItemView, "key" | "linkId">, keyOrLinkId: string): boolean {
	return item.key === keyOrLinkId || item.linkId === keyOrLinkId
}

/** The address of one of the fixed pages. */
export function drivePagePath(page: DrivePage): string {
	return `${PathPrefix}/${DRIVE_PAGES[page]}`
}

/**
 * The trash with its own search run for a term (DS3-D5): the term is in the address, so the Search
 * page's *Search the trash* and the browser's Back both land on the answer.
 */
export function trashSearchPath(term: string): string {
	return term === ""
		? drivePagePath("trash")
		: `${drivePagePath("trash")}?q=${encodeURIComponent(term)}`
}

/** The address of a space, a folder, a document or a file, by its key. */
export function drivePath(key: string): string {
	return `${PathPrefix}/${encodeURIComponent(key)}`
}

/** A file's address with one of its versions open in the preview. */
export function fileVersionPath(key: string, version: number): string {
	return `${drivePath(key)}?file=${String(version)}`
}

/** The administrators' Other shared drives. */
export const ADMIN_DRIVE_PATH = `/admin/${ADMIN_DRIVE_SEGMENT}`

/**
 * The key or the link id an address under `/drive/` names — its second segment, decoded — or "" when
 * it names neither: `/drive` itself, a fixed page, or a segment of neither shape. A trailing `/<slug>`
 * is ignored, so a link that carries a document's name still opens the document after a rename.
 */
export function keyOfPath(pathname: string): string {
	const [first, prefix, segment] = pathname.split("/")
	if (first !== "" || `/${prefix ?? ""}` !== PathPrefix) return ""
	let key: string
	try {
		key = decodeURIComponent(segment ?? "")
	} catch {
		return ""
	}
	return isDriveKey(key) || isLinkId(key) ? key : ""
}
